/**
 * ContractsList — laedt Vertraege fuer eine Property und rendert sie als Liste.
 * Aktive zuerst, dann läuft-in-90-Tagen, dann archived/cancelled.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { I } from '@/shared/icons';
import { Button } from '@/shared/components';
import { toApiError, useApi, type ContractResponse } from '@/shared/api';
import { ContractRow } from './ContractRow';
import { isContractExpiringSoon } from './akteDomainPresenters';

interface Props {
  propertyId: string;
  onDownload: (propertyFileId: string) => void;
  onPreview?: (propertyFileId: string) => void;
  onEdit?: (contract: ContractResponse) => void;
  onUploadHere: () => void;
  displayMode?: 'list' | 'preview';
  lookupFile?: (fileId: string) => import('@/shared/api').PropertyFileResponse | null;
}

export function ContractsList({
  propertyId,
  onDownload,
  onPreview,
  onEdit,
  onUploadHere,
  displayMode = 'list',
  lookupFile,
}: Props) {
  const api = useApi();
  const qc = useQueryClient();

  const contracts = useQuery({
    queryKey: ['contracts', propertyId],
    queryFn: () => api.contracts.list(propertyId),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.contracts.cancel(propertyId, id),
    onSuccess: () => {
      toast.success('Vertrag gekündigt');
      void qc.invalidateQueries({ queryKey: ['contracts', propertyId] });
      void qc.invalidateQueries({ queryKey: ['propertyFiles'] });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.contracts.delete(propertyId, id),
    onSuccess: () => {
      toast.success('Vertrag gelöscht');
      void qc.invalidateQueries({ queryKey: ['contracts', propertyId] });
      void qc.invalidateQueries({ queryKey: ['propertyFiles'] });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  if (contracts.isLoading) {
    return <p className="text-ink-faint text-xs">Lade Verträge…</p>;
  }
  if (contracts.error || !contracts.data) {
    return <p className="text-sealwax text-xs">Verträge konnten nicht geladen werden.</p>;
  }

  const sorted = sortContracts(contracts.data);
  if (sorted.length === 0) {
    return <EmptyHint onUpload={onUploadHere} />;
  }

  return (
    <ul className="space-y-0.5">
      {sorted.map((c) => (
        <ContractRow
          key={c.id}
          contract={c}
          onDownload={onDownload}
          onPreview={onPreview}
          onEdit={onEdit}
          onCancel={(x) => cancel.mutate(x.id)}
          onDelete={(x) => remove.mutate(x.id)}
          propertyId={propertyId}
          displayMode={displayMode}
          file={lookupFile ? lookupFile(c.propertyFileId) : null}
        />
      ))}
    </ul>
  );
}

function sortContracts(items: ContractResponse[]): ContractResponse[] {
  return [...items].sort((a, b) => {
    const aw = weight(a);
    const bw = weight(b);
    if (aw !== bw) return aw - bw;
    return a.counterparty.localeCompare(b.counterparty);
  });
}

function weight(c: ContractResponse): number {
  if (c.status === 'Active' && isContractExpiringSoon(c)) return 0;
  if (c.status === 'Active') return 1;
  if (c.status === 'Archived') return 2;
  return 3;
}

function EmptyHint({ onUpload }: { onUpload: () => void }) {
  return (
    <div className="border-line/60 rounded-md border border-dashed px-4 py-8 text-center">
      <p className="text-ink-soft mb-3 text-sm">
        Noch kein Vertrag hinterlegt. Mietvertrag, Strom, Internet und Versicherung gehören hierher.
      </p>
      <Button variant="brand" leftIcon={<I name="Plus" size={12} />} onClick={onUpload}>
        Verträge hochladen
      </Button>
    </div>
  );
}
